import React, { useEffect, useMemo, useRef } from "react";
import { useOutletContext } from "react-router";
import {
  closeGuide,
  noteFirstRun,
  useGuideStore,
} from "@/components/guide/guides";
import { SpotlightTour, type TourStep } from "@/components/guide/SpotlightTour";
import { useFirstVisit } from "@/components/guide/useFirstVisit";
import { useRailOpen } from "@/components/main/utils/railOpen";
import { GuideStory } from "./GuideStory";

const all = (selector: string) => [...document.querySelectorAll(selector)];
const sidebar = () => all("#main-sidebar");
const newModelButton = () =>
  all("[data-main-pane] button").filter(
    (b) => b.textContent?.trim() === "New model",
  );

/** Opens on the first visit of someone who has never opened a model. */
export function OverviewGuide({
  ready,
  isNew,
}: {
  ready: boolean;
  isNew: boolean;
}): React.JSX.Element | null {
  const open = useGuideStore((s) => s.open);
  useFirstVisit("overview", ready && isNew);
  useEffect(() => {
    if (ready && isNew) noteFirstRun();
  }, [ready, isNew]);

  // Spaces live in the open rail; the tour opens it rather than point at nothing.
  const railOpen = useRef(false);
  railOpen.current = useRailOpen();
  const outlet = useOutletContext<{ toggleSidebar?: () => void } | undefined>();
  const toggle = useRef(outlet?.toggleSidebar);
  toggle.current = outlet?.toggleSidebar;

  const steps = useMemo<TourStep[]>(() => {
    const openRail = () => {
      if (!railOpen.current) toggle.current?.();
    };
    return [
      {
        title: "Everything starts here",
        body: "Fold the sidebar with the logo whenever you want more room.",
        target: sidebar,
        side: "right",
        pad: -1,
        prepare: openRail,
      },
      {
        title: "Your work lives in spaces",
        body: "Spaces organize your personal work, or your team\u2019s projects in an organization.",
        // The Spaces item and the spaces under it.
        target: () => {
          const heading = document
            .querySelector('#main-sidebar [aria-label$=" all spaces"]')
            ?.closest("li");
          const rows = all("#main-sidebar [data-space-id]").slice(0, 4);
          return rows.length
            ? [...(heading ? [heading] : []), ...rows]
            : sidebar();
        },
        side: "right",
        pad: 0,
        prepare: openRail,
        illustration: <GuideStory chapter={1} />,
      },
      {
        title: "A space holds models",
        body: "A model is a workspace of related tables on a shared canvas.",
        target: () => all("[data-naming-anchor]"),
        side: "below",
        illustration: <GuideStory chapter={2} />,
      },
      {
        title: "A model holds tables",
        body: "A table holds your data in typed columns and rows.\nLinks connect tables, even across models.",
        target: newModelButton,
        side: "below",
        pad: -1,
        illustration: <GuideStory chapter={3} />,
      },
    ];
  }, []);

  if (open?.guide !== "overview") return null;
  return (
    <SpotlightTour
      label="Getting started"
      steps={steps}
      welcome={{
        title: "Welcome to Mycorr!",
        body: "We\u2019re so glad you\u2019re here! Mycorr is where your team brings its data together, shapes it side by side and keeps every version safe.\nLet\u2019s take a quick look around. It only takes a minute.",
        illustration: <GuideStory chapter={0} />,
      }}
      skipWelcome={open.direct}
      onClose={() => closeGuide("overview")}
    />
  );
}
